<template>
  <div class="scanner">
    <ion-row v-if="!hideControls" class="ion-justify-content-between ion-align-items-center">
      <ion-button :disabled="active" @click="start">Iniciar escaneo</ion-button>
      <ion-button :disabled="!active" color="medium" @click="stop">Parar</ion-button>
    </ion-row>
    <div :id="containerId" style="max-width: 500px; margin-top: 12px"></div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Html5QrcodeScanner, Html5QrcodeScanType, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import eventBus from '@/event-bus';

export default defineComponent({
  name: 'ScannerHtml5Qrcode',
  props: {
    elementId: { type: String, default: 'scanner' },
    fps: { type: Number, default: 10 },
    qrbox: { type: [Number, Object], default: () => ({ width: 250, height: 250 }) },
    rememberLastUsedCamera: { type: Boolean, default: true },
    showTorchButtonIfSupported: { type: Boolean, default: true },
    autoStopOnSuccess: { type: Boolean, default: true },
    formats: { type: Array, default: () => [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_39] },
    autostart: { type: Boolean, default: false },
    hideControls: { type: Boolean, default: false }
  },
  emits: ['code-scanned'],
  data() {
    return { scanner: undefined, active: false };
  },
  computed: {
    containerId() { return `reader-${this.elementId}`; }
  },
  methods: {
    async start() {
      try {
        // Verificar que el elemento DOM exista antes de continuar
        const element = document.getElementById(this.containerId);
        if (!element) {
          console.error(`[ScannerHtml5Qrcode] Elemento '${this.containerId}' no encontrado en el DOM`);
          this.active = false;
          return;
        }

        const config = {
          fps: this.fps,
          qrbox: this.qrbox,
          rememberLastUsedCamera: this.rememberLastUsedCamera,
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          showTorchButtonIfSupported: this.showTorchButtonIfSupported,
          formatsToSupport: this.formats
        };
        
        const onScanSuccess = (decodedText) => {
          this.$emit('code-scanned', decodedText);
          eventBus().emitter.emit(`scanner:code:${this.elementId}`, { texto: decodedText });
          // evitamos clear() inmediato para no provocar condiciones de carrera en el UI interno
          // El componente padre (modal) detiene explícitamente el escáner antes de cerrar
          if (this.autoStopOnSuccess) this.active = false;
        };
        
        const onScanFailure = () => {};
        
        // Crear escáner con manejo de errores interno
        this.scanner = new Html5QrcodeScanner(this.containerId, config, false);
        
        // Verificar que el escáner se creó correctamente antes de renderizar
        if (!this.scanner) {
          console.error('[ScannerHtml5Qrcode] No se pudo crear el escáner');
          this.active = false;
          return;
        }
        
        this.scanner.render(onScanSuccess, onScanFailure);
        this.active = true;
      } catch (e) {
        console.error('[ScannerHtml5Qrcode] Error en start():', e);
        this.active = false;
      }
    },
    async stop() {
      try { 
        if (this.scanner) {
          await this.scanner.clear();
          this.scanner = undefined;
        }
      } catch (e) {
        console.error('[ScannerHtml5Qrcode] Error en stop():', e);
        // En caso de error, aseguramos que el estado se limpie
        this.scanner = undefined;
      }
      this.active = false;
    }
  },
  async mounted() {
    // inicio automático del escaneo si se solicita
    if (this.autostart) {
      try {
        // Esperar a que el DOM esté completamente listo
        await this.$nextTick();
        await this.start();
      } catch (e) {
        console.error('[ScannerHtml5Qrcode] Error en mounted():', e);
        this.active = false;
      }
    }
  },
  async unmounted() {
    try {
      if (this.scanner) {
        await this.scanner.clear();
        this.scanner = undefined;
      }
    } catch (e) {
      console.error('[ScannerHtml5Qrcode] Error en unmounted():', e);
    }
    this.active = false;
  }
});
</script>

<style scoped>
.scanner { width: 100%; }
</style>